import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Hero3D from "@/components/Hero3D";
import Stats from "@/components/Stats";
import VideoShowcase from "@/components/VideoShowcase";
// import Testimonials from "@/components/Testimonials";
import Faq from "@/components/Faq";
import TrustBadges from "@/components/TrustBadges";
import TechStack from "@/components/TechStack";
// import SectionNavigator from "@/components/SectionNavigator";
// import ClientsSection from "@/components/ClientsSection"; // Hidden clients section
import { ChevronDown, ChevronUp } from "lucide-react";

const Index = () => {
  // Function to handle smooth scrolling to sections
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Calculate exact position to prevent overshooting
      const headerOffset = 80; // Adjust if needed based on any fixed headers
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      // Use scrollTo for more controlled scrolling behavior
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Standardized button classes for all navigation arrows
  const navButtonClass = "flex items-center justify-center w-7 h-7 rounded-full bg-primary/50 hover:bg-primary/70 text-white shadow-md backdrop-blur-sm transition-all duration-300";

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Mai Tech - AI Solutions & Viral Apps | Transform Your Business</title>
        <meta 
          name="description" 
          content="Mai Tech specializes in building AI solutions and viral mobile apps. Transform your business with our cutting-edge technology and innovative digital solutions." 
        />
        <meta 
          name="keywords" 
          content="AI solutions, mobile apps, viral apps, Mai Tech, artificial intelligence, business transformation, technology solutions" 
        />
        <meta property="og:title" content="Mai Tech - AI Solutions & Viral Apps" />
        <meta 
          property="og:description" 
          content="Transform your business with Mai Tech's AI solutions and viral mobile apps. Cutting-edge technology for modern businesses." 
        />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://maitechcorp.com" />
      </Helmet>
      
      <Navbar />
      
      {/* Hero section */}
      <section id="hero" className="relative">
        <Hero3D />
        
        {/* Down arrow to TechStack section - skip team section */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
          <button 
            aria-label="Scroll to TechStack section"
            className={navButtonClass}
            onClick={() => scrollToSection('techstack')}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </section>
      
      {/* Content sections */}
      <div className="relative w-full">
        <img
          src="/lovable-uploads/8aaea022-1f37-419e-b233-09643a330fcd.png"
          alt="Technology Background"
          className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-overlay"
        />
        <div className="relative z-10">
          {/* Tech Stack section */}
          <section id="techstack" className="relative">
            {/* Desktop navigation arrows for TechStack section */}
            <div className="absolute top-20 left-1/2 transform animate-bounce z-20">
              <button 
                aria-label="Scroll to Hero section"
                className={`${navButtonClass}`}
                onClick={() => scrollToSection('hero')}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            
            <TechStack />
          </section>
        </div>
      </div>

      {/* FAQ Section - shown only on homepage */}
      <div className="bg-background">
        <Faq />
      </div>

      {/* Stats Section */}
      <Stats />

      {/* Trust Badges */}
      <TrustBadges />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
